import React, { useEffect, useState, useContext } from "react";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import FadeLoader from "react-spinners/FadeLoader";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineMail } from "react-icons/ai";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  customer_register,
  messageClear,
  verify_email,
  resend_verification,
} from "../store/reducers/authReducer";
import { LanguageContext } from "../context/LanguageContext";

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loader, successMessage, errorMessage, userInfo } = useSelector(
    (state) => state.auth
  );
  const { t } = useContext(LanguageContext);

  const [step, setStep] = useState(1); // 1 = Registration, 2 = Verification
  const [countdown, setCountdown] = useState(0);
  const [emailSent, setEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [otp, setOtp] = useState({
    digit1: "",
    digit2: "",
    digit3: "",
    digit4: "",
    digit5: "",
    digit6: "",
  });

  const [state, setState] = useState({
    name: "",
    email: "",
    password: "",
  });

  const inputHandle = (e) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  const otpHandle = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value) && value.length <= 1) {
      setOtp({ ...otp, [name]: value });
      if (value && name !== "digit6") {
        const nextInput = document.querySelector(
          `input[name=digit${parseInt(name.replace("digit", "")) + 1}]`
        );
        if (nextInput) nextInput.focus();
      }
    }
  };

  const register = (e) => {
    e.preventDefault();
    dispatch(customer_register(state));
  };

  const verifyEmail = (e) => {
    e.preventDefault();
    const code = Object.values(otp).join("");
    if (code.length !== 6) {
      toast.error("Please enter a 6-digit verification code");
      return;
    }
    dispatch(verify_email({ email: state.email, verificationCode: code }));
  };

  const resendVerification = () => {
    if (countdown > 0) return;
    dispatch(resend_verification({ email: state.email }));
    setCountdown(60);
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());

      if (successMessage.includes("verification")) {
        setStep(2);
        setEmailSent(true);
        setCountdown(60);
      } else if (successMessage.includes("Verified")) {
        navigate("/");
      }
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage]);

  useEffect(() => {
    if (userInfo?.verified) {
      navigate("/");
    }
  }, [userInfo]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Password validation logic
  const validatePassword = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLengthValid = password.length > 8;

    return {
      hasUppercase,
      hasLowercase,
      hasSpecialChar,
      isLengthValid,
    };
  };

  const getPasswordStrengthMessage = () => {
    const password = state.password;
    const { hasUppercase, hasLowercase, hasSpecialChar, isLengthValid } =
      validatePassword(password);

    if (!hasUppercase || !hasLowercase || !hasSpecialChar || !isLengthValid) {
      return "Password must contain at least 1 uppercase, 1 lowercase, 1 special character, and be longer than 8 characters";
    } else if (password.length <= 10) {
      return "Moderate";
    } else {
      return "Strong";
    }
  };

  const isPasswordValid = () => {
    const password = state.password;
    const { hasUppercase, hasLowercase, hasSpecialChar, isLengthValid } =
      validatePassword(password);

    return hasUppercase && hasLowercase && hasSpecialChar && isLengthValid;
  };

  return (
    <div>
      {loader && (
        <div className="fixed inset-0 z-[999] bg-[#38303033] flex items-center justify-center">
          <FadeLoader />
        </div>
      )}
      <Headers />
      <div className="bg-slate-200 mt-4">
        <div className="max-w-[1440px] mx-auto px-6 py-10">
          <div className="bg-white shadow-md rounded-lg grid grid-cols-2 sm:grid-cols-1 mx-auto w-[60%] md:w-full">
            <div className="p-8">
              {/* Progress Bar */}
              <div className="flex items-center justify-center mb-6">
                <div
                  className={`w-6 h-6 rounded-full ${
                    step === 1 ? "bg-purple-500" : "bg-gray-300"
                  }`}
                ></div>
                <div className="w-10 h-1 bg-gray-300 mx-2"></div>
                <div
                  className={`w-6 h-6 rounded-full ${
                    step === 2 ? "bg-purple-500" : "bg-gray-300"
                  }`}
                ></div>
              </div>

              {/* Step 1 - Register Form */}
              <div
                className={`${
                  step === 1 ? "block" : "hidden"
                } transition-all duration-500 ease-in-out`}
              >
                <h2 className="text-xl font-bold text-center text-slate-600 mb-6">
                  {t("register")}
                </h2>
                <form onSubmit={register} className="text-slate-600">
                  <div className="mb-4">
                    <label htmlFor="name">{t("name")}</label>
                    <input
                      onChange={inputHandle}
                      value={state.name}
                      type="text"
                      name="name"
                      id="name"
                      placeholder={t("name")}
                      className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md outline-none focus:border-purple-500"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="email">{t("emailLabel")}</label>
                    <input
                      onChange={inputHandle}
                      value={state.email}
                      type="email"
                      name="email"
                      id="email"
                      placeholder={t("emailPlaceholder")}
                      className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md outline-none focus:border-purple-500"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="password">{t("passwordLabel")}</label>
                    <div className="relative flex items-center">
                      <input
                        onChange={inputHandle}
                        value={state.password}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        id="password"
                        placeholder={t("passwordPlaceholder")}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md pr-10 outline-none"
                        required
                      />
                      <span
                        className="absolute right-3 cursor-pointer text-gray-500"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                    {/* Password Strength */}
                    {state.password && (
                      <div className="mt-2 text-sm">
                        <div
                          className={`h-2 rounded-md ${
                            isPasswordValid() ? "bg-green-500" : "bg-red-400"
                          }`}
                        ></div>
                        <span className="text-xs text-gray-500">
                          {getPasswordStrengthMessage()}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition"
                    disabled={loader || !isPasswordValid()} // Disable submit if password is invalid
                  >
                    {loader ? t("processing") : t("register")}
                  </button>
                </form>
                <p className="mt-4 text-center text-sm text-slate-500">
                  {t("alreadyHaveAccount")}{" "}
                  <Link className="text-blue-500" to="/login">
                    {t("login")}
                  </Link>
                </p>
              </div>

              {/* Step 2 - OTP Verification */}
              <div
                className={`${
                  step === 2 ? "block" : "hidden"
                } transition-all duration-500 ease-in-out`}
              >
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      <AiOutlineMail className="text-2xl text-purple-500" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-slate-700">
                    {t("verifyEmailTitle")}
                  </h2>
                  <p className="text-slate-500 mt-2">
                    {t("verificationSentTo")}{" "}
                    <span className="font-medium">{state.email}</span>
                  </p>
                </div>
                <form onSubmit={verifyEmail}>
                  <div className="flex justify-center gap-2 mb-6">
                    {[1, 2, 3, 4, 5, 6].map((digit) => (
                      <input
                        key={digit}
                        type="text"
                        name={`digit${digit}`}
                        value={otp[`digit${digit}`]}
                        onChange={otpHandle}
                        className="w-12 h-12 border border-slate-300 rounded-md text-center text-xl focus:border-purple-500"
                        maxLength={1}
                        required
                      />
                    ))}
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2 bg-purple-500 text-white rounded-md mb-4 hover:bg-purple-600 transition"
                    disabled={loader}
                  >
                    {loader ? t("verifying") : t("verifyEmail")}
                  </button>
                  <p className="text-center">
                    <button
                      type="button"
                      onClick={resendVerification}
                      className={`text-purple-500 font-medium ${
                        countdown > 0 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={countdown > 0}
                    >
                      {t("resend")} {countdown > 0 ? `(${countdown}s)` : ""}
                    </button>
                  </p>
                </form>
              </div>
            </div>
            <img className="w-full h-[95%]" src="/images/login.jpg" alt="" />

            {/* Right Image (Mobile Version) */}
            <div className="hidden md:block w-[40%]">
              <img
                src="/images/login.jpg"
                alt="Register"
                className="w-full h-full object-cover rounded-r-lg"
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Register;
